/**
 * One draft-job row in the "Devices" card of the Check-In create page (and the
 * per-device edit form). A design-system EditorCard: disclosure header with the
 * device label + running charge + a recessive remove action, and a body of
 * radio-option pickers (device type / service tier / mode / urgency) + FormField
 * text fields + the preliminary-charges composer.
 */

import React, { useRef, useState } from 'react';
import { IconTrash, IconChevronDown, IconDevices } from '@tabler/icons-react';
import {
  CHECK_IN_DEVICE_TYPE_OTHER_MAX_LENGTH,
  type CheckInChargeLine,
  type CheckInDeviceType,
} from '@techgitter/contracts';

import { EditorCard } from '../admin/EditorCard';
import { RadioOption } from '../panel/RadioOption';
import { FormField } from '../panel/FormField';
import { Chip } from '../chip';
import { ActionPicker } from '../dropdown';
import {
  CHECK_IN_EXPEDITE_TIERS,
  CHECK_IN_SERVICE_TIERS,
  type CheckInExpediteTier,
  type CheckInServiceMode,
  type CheckInServiceTier,
} from '../../data/checkInServices';
import { getProductBySku, useProductCatalog } from '../../hooks/useProductCatalog';
import { PreliminaryChargesEditor } from './PreliminaryChargesEditor';
import {
  CHECK_IN_DEVICE_TYPE_LABELS,
  DEVICE_TYPE_ICON,
  DeviceTypeIcon,
  MORE_DEVICE_TYPES,
  PRIMARY_DEVICE_TYPES,
  checkInDeviceLabel,
} from './deviceTypes';
import './DraftJobAccordionRow.css';

export interface DraftJobFormState {
  clientId: string;
  deviceType: CheckInDeviceType;
  /** What an `other` device is ("Drone"). Required then; ignored otherwise. */
  deviceTypeOther: string;
  serviceMode: CheckInServiceMode;
  serviceTier: CheckInServiceTier['id'];
  urgency: CheckInExpediteTier['id'];
  serviceSummary: string;
  requirements: string;
  customerVisibleDetails: string;
  internalNotes: string;
  preliminaryCharges: CheckInChargeLine[];
}

/**
 * A blank device row. Shared by the create page's "Add another device" and the
 * session-detail Add Device sheet so a device added later starts from exactly
 * the same defaults as one added during creation — the preliminary-charges
 * composer auto-seeds the diagnostic line from here.
 */
export function createDraftJobFormState(): DraftJobFormState {
  return {
    clientId: `cli-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    deviceType: 'laptop' as CheckInDeviceType,
    deviceTypeOther: '',
    serviceMode: 'diagnose_and_quote',
    serviceTier: 'repair',
    urgency: 'standard',
    serviceSummary: '',
    requirements: '',
    customerVisibleDetails: '',
    internalNotes: '',
    preliminaryCharges: [],
  };
}

export interface DraftJobAccordionRowProps {
  index: number;
  job: DraftJobFormState;
  expanded: boolean;
  showRemove: boolean;
  /** When true, hide the service-tier / urgency / service-mode pickers and
   * the preliminary-charges editor. Express check-ins have no money to
   * collect, so those sections aren't relevant. */
  isExpress?: boolean;
  /** Session-level context plumbed through to PreliminaryChargesEditor
   * so it can apply the return-grace logic. Both are optional —
   * non-return check-ins ignore them entirely. */
  intakeType?: import('@techgitter/contracts').CheckInIntakeType | null;
  originalWorkItemId?: string | null;
  /** Pre-charges editor uses this to suppress the auto-diagnostic line
   * for Core / Core+ customers (their work is billed monthly, not at
   * check-in). True only when the picked customer's designation is
   * "Core" or "Core+"; the receptionist can still opt in per-device. */
  isCoreCustomer?: boolean;
  /**
   * This device inherits the job's service decisions from device 1, so none of
   * them are asked again: Service Summary, Service Tier, Service Mode, Urgency,
   * and the preliminary charges.
   *
   * Set for devices after the first in `single_job` grouping, where all of
   * those describe the **job** rather than the device — one ticket, one
   * status, one invoice. Asking twice invites two answers to one question and
   * only one of them reaches the ticket; worse, the charges editor would seed a
   * **second diagnostic fee** for a job that is only diagnosed once.
   *
   * The inheritance itself is server-side (`add_draft_job` copies device 1's
   * summary and service mode when they are omitted), so this prop only decides
   * what reception is asked.
   */
  inheritsJobFields?: boolean;
  /**
   * The device already exists. The server does not accept a device-type
   * change, so the picker is shown but cannot be changed — otherwise a switch
   * would quietly re-price the charges for a type that never saves.
   */
  deviceTypeLocked?: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<DraftJobFormState>) => void;
  onChargesChange: (charges: CheckInChargeLine[]) => void;
  onRemove: () => void;
}


const aud = (value: number) =>
  value.toLocaleString(undefined, { style: 'currency', currency: 'AUD' });

export const DraftJobAccordionRow: React.FC<DraftJobAccordionRowProps> = ({
  index,
  job,
  expanded,
  showRemove,
  isExpress = false,
  intakeType = null,
  originalWorkItemId = null,
  isCoreCustomer = false,
  inheritsJobFields = false,
  deviceTypeLocked = false,
  onToggle,
  onChange,
  onChargesChange,
  onRemove,
}) => {
  // Subscribe to the product catalog so this row re-renders when prices load.
  // Lookups use the synchronous getProductBySku helper below.
  useProductCatalog();
  const chargesTotal = job.preliminaryCharges.reduce((sum, c) => sum + (c.finalTotal || 0), 0);
  // Service Summary is the one mandatory field on a device — the only one the
  // server refuses a draft job without (`serviceSummary is required`), since
  // device type, tier, mode and urgency all arrive pre-selected. Marked here
  // rather than only on a rejected save: the row is often collapsed on the
  // create page, so the header badge is what says which device is unfinished.
  const summaryOutstanding = !inheritsJobFields && !job.serviceSummary.trim();
  // Express has no money to collect; an inheriting device's tier, mode,
  // urgency and charges all belong to the job and were set on device 1.
  const hideServiceChoices = isExpress || inheritsJobFields;
  // Native radios group by `name`, so scope each group's name to this row —
  // otherwise selecting a device type in one row would clear another.
  const g = (group: string) => `${group}-${job.clientId}`;
  // The fourth tile: every less common type sits behind it in one menu, so
  // the row never grows past four controls however many types are added.
  // Once one is picked the tile takes that device's icon and name.
  const moreTileRef = useRef<HTMLButtonElement>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const selectedIsMoreDevice = MORE_DEVICE_TYPES.includes(job.deviceType);
  const otherOutstanding = job.deviceType === 'other' && !job.deviceTypeOther.trim();

  return (
    <EditorCard
      expanded={expanded}
      onToggle={onToggle}
      title={
        <>
          <span className="draft-job__title-icon">
            <DeviceTypeIcon deviceType={job.deviceType} />
          </span>
          <span className="draft-job__title-text">
            Device {index + 1}: {checkInDeviceLabel(job)}
            {job.serviceSummary ? ` — ${job.serviceSummary}` : ' — Untitled'}
          </span>
          {summaryOutstanding && <Chip tone="warning">Needs summary</Chip>}
          {!hideServiceChoices && <Chip tone="neutral">{aud(chargesTotal)}</Chip>}
        </>
      }
      actions={
        showRemove ? (
          <button
            type="button"
            className="editor-icon-btn editor-icon-btn--danger"
            aria-label="Remove device"
            title="Remove device"
            onClick={onRemove}
          >
            <IconTrash size={16} stroke={1.5} />
          </button>
        ) : undefined
      }
    >
      <div className="draft-job__group">
        <div className="editor-section-title">Device Type</div>
        <div className="radio-option-group radio-option-group--row">
          {PRIMARY_DEVICE_TYPES.map(dt => (
            <RadioOption
              key={dt}
              name={g('deviceType')}
              value={dt}
              checked={job.deviceType === dt}
              onChange={v => onChange({ deviceType: v as CheckInDeviceType })}
              icon={<DeviceTypeIcon deviceType={dt} />}
              label={CHECK_IN_DEVICE_TYPE_LABELS[dt]}
              disabled={deviceTypeLocked && job.deviceType !== dt}
            />
          ))}
          <button
            ref={moreTileRef}
            type="button"
            className={`radio-option draft-job__more-tile ${
              selectedIsMoreDevice ? 'radio-option--selected' : ''
            } ${deviceTypeLocked && !selectedIsMoreDevice ? 'radio-option--disabled' : ''}`}
            disabled={deviceTypeLocked}
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(o => !o)}
          >
            <span
              className={`draft-job__more-dot ${selectedIsMoreDevice ? 'draft-job__more-dot--on' : ''}`}
              aria-hidden
            />
            <span className="radio-option__icon">
              {selectedIsMoreDevice ? (
                <DeviceTypeIcon deviceType={job.deviceType} />
              ) : (
                <IconDevices size={16} stroke={1.5} />
              )}
            </span>
            <span className="radio-option__text">
              <span className="radio-option__label">
                {selectedIsMoreDevice ? CHECK_IN_DEVICE_TYPE_LABELS[job.deviceType] : 'Other device'}
              </span>
            </span>
            {!deviceTypeLocked && (
              <IconChevronDown
                size={14}
                stroke={1.5}
                className={`draft-job__more-chev ${moreOpen ? 'draft-job__more-chev--open' : ''}`}
              />
            )}
          </button>
          {moreOpen && (
            <ActionPicker
              anchorRef={moreTileRef}
              currentValue={job.deviceType}
              options={MORE_DEVICE_TYPES.map(dt => ({
                value: dt,
                label: CHECK_IN_DEVICE_TYPE_LABELS[dt],
                Icon: DEVICE_TYPE_ICON[dt],
              }))}
              onSelect={v => onChange({ deviceType: v as CheckInDeviceType })}
              onClose={() => setMoreOpen(false)}
            />
          )}
        </div>
        {job.deviceType === 'other' && (
          <FormField label="What is it?" required outstanding={otherOutstanding}>
            <input
              type="text"
              value={job.deviceTypeOther}
              maxLength={CHECK_IN_DEVICE_TYPE_OTHER_MAX_LENGTH}
              placeholder="e.g. Drone, Smartwatch, NAS"
              onChange={e => onChange({ deviceTypeOther: e.target.value })}
            />
          </FormField>
        )}
      </div>

      {!hideServiceChoices && (
        <div className="draft-job__group">
          <div className="editor-section-title">Service Tier</div>
          <div className="radio-option-group">
            {CHECK_IN_SERVICE_TIERS.map(t => (
              <RadioOption
                key={t.id}
                name={g('serviceTier')}
                value={t.id}
                checked={job.serviceTier === t.id}
                onChange={v => onChange({ serviceTier: v as CheckInServiceTier['id'] })}
                label={t.label}
                description={t.description}
              />
            ))}
          </div>
        </div>
      )}

      {!hideServiceChoices && (
        <div className="draft-job__group">
          <div className="editor-section-title">Service Mode</div>
          <div className="radio-option-group">
            <RadioOption
              name={g('serviceMode')}
              value="diagnose_and_quote"
              checked={job.serviceMode === 'diagnose_and_quote'}
              onChange={v => onChange({ serviceMode: v as CheckInServiceMode })}
              label="Diagnose & Quote"
              description="Customer pays a diagnostic fee. We assess and send a quote."
            />
            <RadioOption
              name={g('serviceMode')}
              value="prepaid_in_full"
              checked={job.serviceMode === 'prepaid_in_full'}
              onChange={v => onChange({ serviceMode: v as CheckInServiceMode })}
              label="Pre-paid in Full"
              description="Customer pays the full service cost upfront. No diagnostic fee."
            />
          </div>
        </div>
      )}

      {!hideServiceChoices && (
        <div className="draft-job__group">
          <div className="editor-section-title">Urgency</div>
          <div className="radio-option-group">
            {CHECK_IN_EXPEDITE_TIERS.map(t => {
              const priceLabel = expeditePriceLabel(t);
              return (
                <RadioOption
                  key={t.id}
                  name={g('urgency')}
                  value={t.id}
                  checked={job.urgency === t.id}
                  onChange={v => onChange({ urgency: v as CheckInExpediteTier['id'] })}
                  label={
                    <>
                      {t.label}
                      {priceLabel && <span className="draft-job__price"> ({priceLabel})</span>}
                    </>
                  }
                  description={t.description}
                />
              );
            })}
          </div>
        </div>
      )}

      {inheritsJobFields ? (
        <div className="editor-hint">
          Service summary, tier, mode, urgency and charges all come from the first
          device — this check-in is one job.
        </div>
      ) : (
        <>
          <FormField label="Service Summary" required outstanding={summaryOutstanding}>
            <textarea
              rows={2}
              value={job.serviceSummary}
              onChange={e => onChange({ serviceSummary: e.target.value })}
              placeholder="eg. MacBook won't boot"
            />
          </FormField>
          <div className="editor-hint">Customer-visible</div>
        </>
      )}

      <FormField label="Requirements">
        <textarea
          rows={2}
          value={job.requirements}
          onChange={e => onChange({ requirements: e.target.value })}
          placeholder="eg. Need computer upgraded to Windows 11 and OneDrive setup to backup"
        />
      </FormField>
      <div className="editor-hint">Customer-visible</div>

      <FormField label="Details">
        <textarea
          rows={2}
          value={job.customerVisibleDetails}
          onChange={e => onChange({ customerVisibleDetails: e.target.value })}
          placeholder="eg. Battery was dying quickly, now laptop only works when connected to adapter"
        />
      </FormField>
      <div className="editor-hint">Customer-visible</div>

      <FormField label="Internal Notes">
        <textarea
          rows={2}
          value={job.internalNotes}
          onChange={e => onChange({ internalNotes: e.target.value })}
          placeholder="eg. Customer is due to go on holiday and won't be contactable until they return"
        />
      </FormField>
      <div className="editor-hint">Staff-only — never shown to the customer</div>

      {!hideServiceChoices && (
        <PreliminaryChargesEditor
          deviceType={job.deviceType}
          serviceMode={job.serviceMode}
          serviceTier={job.serviceTier}
          urgency={job.urgency}
          charges={job.preliminaryCharges}
          onChange={onChargesChange}
          intakeType={intakeType}
          originalWorkItemId={originalWorkItemId}
          isCoreCustomer={isCoreCustomer}
        />
      )}
    </EditorCard>
  );
};

function expeditePriceLabel(tier: CheckInExpediteTier): string | null {
  if (!tier.productCode) return null;
  const product = getProductBySku(tier.productCode);
  if (!product) return null;
  return `+${aud(product.unitCost)}`;
}
